import { Controller } from "@hotwired/stimulus"

const DAY_NAMES = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"]
const DEFAULT_STATE = () => [
  { open: "09:00", close: "18:00", closed: false, breaks: [] },
  { open: "09:00", close: "18:00", closed: false, breaks: [] },
  { open: "09:00", close: "18:00", closed: false, breaks: [] },
  { open: "09:00", close: "18:00", closed: false, breaks: [] },
  { open: "09:00", close: "18:00", closed: false, breaks: [] },
  { open: "", close: "", closed: true, breaks: [] },
  { open: "", close: "", closed: true, breaks: [] }
]

export default class extends Controller {
  static targets = ["input", "content", "backdrop", "header", "days", "manualLink"]

  connect() {
    this.boundCloseOnEscape = this.closeOnEscape.bind(this)
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    this.manualMode = false
    this.days = DEFAULT_STATE()

    const parsed = this.parseValue(this.inputTarget.value)
    if (parsed) {
      this.days = parsed
      this.applyToInputs()
    } else if (this.inputTarget.value.trim() === "") {
      this.applyToInputs()
      this.updateInput()
    } else {
      this.manualMode = true
      this.applyToInputs()
    }

    if (this.manualMode) {
      this.inputTarget.readOnly = false
      this.hideManualLink()
    }
  }

  disconnect() {
    this.teardownGlobalListeners()
  }

  open(event) {
    if (event) event.preventDefault()
    this.contentTarget.classList.remove("hidden")
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("hidden")
    }
    if (this.isMobile()) {
      document.body.style.overflow = "hidden"
      document.documentElement.style.overflow = "hidden"
    }
    document.addEventListener("keydown", this.boundCloseOnEscape)
    document.addEventListener("click", this.boundCloseOnClickOutside)
  }

  close() {
    this.contentTarget.classList.add("hidden")
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("hidden")
    }
    document.body.style.overflow = ""
    document.documentElement.style.overflow = ""
    this.teardownGlobalListeners()
  }

  teardownGlobalListeners() {
    document.removeEventListener("keydown", this.boundCloseOnEscape)
    document.removeEventListener("click", this.boundCloseOnClickOutside)
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.close()
  }

  closeOnClickOutside(event) {
    if (!this.contentTarget.classList.contains("hidden") && !this.element.contains(event.target)) {
      this.close()
    }
  }

  stopEnter(event) {
    if (event.key === "Enter") {
      event.preventDefault()
    }
  }

  isMobile() {
    return window.innerWidth < 800
  }

  // --- presets ---

  applyPreset(event) {
    event.preventDefault()
    const preset = event.currentTarget.dataset.preset

    switch (preset) {
      case "weekdays":
        this.days = [
          ...Array(5).fill(null).map(() => ({ open: "09:00", close: "18:00", closed: false, breaks: [] })),
          ...Array(2).fill(null).map(() => ({ open: "", close: "", closed: true, breaks: [] }))
        ]
        break
      case "daily":
        this.days = Array(7).fill(null).map(() => ({ open: "08:00", close: "22:00", closed: false, breaks: [] }))
        break
      case "daily23":
        this.days = Array(7).fill(null).map(() => ({ open: "08:00", close: "23:00", closed: false, breaks: [] }))
        break
      case "roundclock":
        this.days = Array(7).fill(null).map(() => ({ open: "00:00", close: "00:00", closed: false, breaks: [] }))
        break
      case "clear":
        this.days = Array(7).fill(null).map(() => ({ open: "", close: "", closed: true, breaks: [] }))
        break
    }

    this.leaveManualMode()
    this.applyToInputs()
    this.updateInput()
  }

  // --- per-day edits ---

  updateDay(event) {
    const row = event.currentTarget.closest("[data-day-index]")
    const idx = parseInt(row.dataset.dayIndex, 10)
    const day = this.days[idx]

    const openInput = row.querySelector("[data-hours-field='open']")
    const closeInput = row.querySelector("[data-hours-field='close']")
    const closedInput = row.querySelector("[data-hours-field='closed']")

    day.closed = closedInput.checked
    day.open = openInput.value
    day.close = closeInput.value

    this.setClosedUI(row, day.closed)
    this.leaveManualMode()
    this.updateInput()
  }

  addBreak(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-day-index]")
    const idx = parseInt(row.dataset.dayIndex, 10)
    this.days[idx].breaks.push({ start: "13:00", end: "14:00" })
    this.renderBreaksForRow(row, idx)
    this.leaveManualMode()
    this.updateInput()
  }

  removeBreak(event) {
    event.preventDefault()
    const button = event.currentTarget
    const breakIndex = parseInt(button.dataset.breakIndex, 10)
    const row = button.closest("[data-day-index]")
    const idx = parseInt(row.dataset.dayIndex, 10)
    this.days[idx].breaks.splice(breakIndex, 1)
    this.renderBreaksForRow(row, idx)
    this.leaveManualMode()
    this.updateInput()
  }

  updateBreak(event) {
    const input = event.currentTarget
    const row = input.closest("[data-day-index]")
    const idx = parseInt(row.dataset.dayIndex, 10)
    const breakIndex = parseInt(input.dataset.breakIndex, 10)
    const field = input.dataset.breakField
    this.days[idx].breaks[breakIndex][field] = input.value
    this.leaveManualMode()
    this.updateInput()
  }

  applyToAll(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-day-index]")
    const idx = parseInt(row.dataset.dayIndex, 10)
    const source = this.days[idx]
    for (let i = 0; i < 7; i++) {
      if (i === idx) continue
      this.days[i] = {
        open: source.open,
        close: source.close,
        closed: source.closed,
        breaks: source.breaks.map(b => ({ ...b }))
      }
    }
    this.applyToInputs()
    this.leaveManualMode()
    this.updateInput()
  }

  // --- manual mode ---

  enableManual(event) {
    event.preventDefault()
    this.manualMode = true
    this.inputTarget.readOnly = false
    this.inputTarget.focus()
    this.hideManualLink()
    this.close()
  }

  leaveManualMode() {
    if (!this.manualMode) return
    this.manualMode = false
    this.inputTarget.readOnly = true
    this.showManualLink()
  }

  hideManualLink() {
    if (this.hasManualLinkTarget) this.manualLinkTarget.classList.add("hidden")
  }

  showManualLink() {
    if (this.hasManualLinkTarget) this.manualLinkTarget.classList.remove("hidden")
  }

  // --- rendering ---

  applyToInputs() {
    this.daysTarget.querySelectorAll("[data-day-index]").forEach((row) => {
      const idx = parseInt(row.dataset.dayIndex, 10)
      const day = this.days[idx]
      const openInput = row.querySelector("[data-hours-field='open']")
      const closeInput = row.querySelector("[data-hours-field='close']")
      const closedInput = row.querySelector("[data-hours-field='closed']")

      openInput.value = day.open
      closeInput.value = day.close
      closedInput.checked = day.closed
      this.setClosedUI(row, day.closed)
      this.renderBreaksForRow(row, idx)
    })
  }

  setClosedUI(row, closed) {
    const work = row.querySelector("[data-hours-work]")
    if (work) work.classList.toggle("hidden", closed)
  }

  renderBreaksForRow(row, idx) {
    const container = row.querySelector("[data-breaks]")
    if (!container) return
    container.innerHTML = this.days[idx].breaks.map((b, bi) => `
      <div class="hours-editor-break-row">
        <span>перерыв</span>
        <input type="time" value="${b.start}" data-break-index="${bi}" data-break-field="start" data-action="change->hours-editor#updateBreak keydown->hours-editor#stopEnter" class="form-input" />
        <span class="hours-editor-day-dash">–</span>
        <input type="time" value="${b.end}" data-break-index="${bi}" data-break-field="end" data-action="change->hours-editor#updateBreak keydown->hours-editor#stopEnter" class="form-input" />
        <button type="button" data-break-index="${bi}" data-action="click->hours-editor#removeBreak" class="hours-editor-break-remove" aria-label="Удалить перерыв">×</button>
      </div>
    `).join("")
  }

  // --- string generation / parsing ---

  updateInput() {
    this.inputTarget.value = this.buildValue()
  }

  buildValue() {
    if (this.days.every(d => d.closed)) return ""
    if (this.days.every(d => !d.closed && d.open === "00:00" && d.close === "00:00" && d.breaks.length === 0)) {
      return "Круглосуточно"
    }

    const descriptors = this.days.map(d => {
      if (d.closed) return "__closed__"
      return JSON.stringify({ open: d.open, close: d.close, breaks: d.breaks })
    })

    const groups = []
    for (let i = 0; i < 7; i++) {
      if (i === 0 || descriptors[i] !== descriptors[i - 1]) {
        groups.push({ start: i, end: i })
      } else {
        groups[groups.length - 1].end = i
      }
    }

    return groups.map(g => {
      const name = g.start === g.end ? DAY_NAMES[g.start] : `${DAY_NAMES[g.start]}–${DAY_NAMES[g.end]}`
      const desc = descriptors[g.start]
      if (desc === "__closed__") return `${name} — выходной`

      const data = JSON.parse(desc)
      let result = `${name} ${data.open}–${data.close}`
      if (data.breaks && data.breaks.length > 0) {
        data.breaks.forEach(b => {
          result += ` (перерыв ${b.start}–${b.end})`
        })
      }
      return result
    }).join(", ")
  }

  parseValue(value) {
    const trimmed = value.trim()
    if (trimmed === "") return DEFAULT_STATE()
    if (trimmed === "Круглосуточно") {
      return Array(7).fill(null).map(() => ({ open: "00:00", close: "00:00", closed: false, breaks: [] }))
    }

    const days = DEFAULT_STATE()
    const segments = trimmed.split(", ")
    let parsedAny = false

    for (const segment of segments) {
      const match = segment.match(/^(Пн|Вт|Ср|Чт|Пт|Сб|Вс)(?:([–-])(Пн|Вт|Ср|Чт|Пт|Сб|Вс))?\s*(.*)$/)
      if (!match) return null

      const startName = match[1]
      const dash = match[2]
      const endName = match[3]
      const rest = match[4].trim()

      const startIdx = DAY_NAMES.indexOf(startName)
      const endIdx = endName ? DAY_NAMES.indexOf(endName) : startIdx
      if (startIdx === -1 || endIdx === -1) return null
      if (endIdx < startIdx) return null

      let schedule
      if (rest.includes("выходной")) {
        schedule = { open: "", close: "", closed: true, breaks: [] }
      } else {
        const timeMatches = rest.matchAll(/(\d{2}:\d{2})[–-](\d{2}:\d{2})/g)
        const times = Array.from(timeMatches)
        if (times.length === 0) return null

        const main = times.shift()
        const breaks = []
        const breakMatches = rest.matchAll(/перерыв\s+(\d{2}:\d{2})[–-](\d{2}:\d{2})/g)
        for (const bm of breakMatches) {
          breaks.push({ start: bm[1], end: bm[2] })
        }
        schedule = { open: main[1], close: main[2], closed: false, breaks }
      }

      for (let i = startIdx; i <= endIdx; i++) {
        days[i] = schedule
      }
      parsedAny = true
    }

    return parsedAny ? days : null
  }
}
